// Feed prefetching
// ----------------
// Warms the tabs the viewer has not opened yet, so swiping to one shows people
// rather than a spinner.
//
// The whole point is to cost nothing at startup, so this obeys three rules:
//
//   1. It never runs during the initial render. Callers schedule it on an idle
//      callback, after the tab the viewer is actually looking at has loaded.
//   2. It runs one request at a time. Four parallel requests would compete
//      with the active feed's own paging for connections, which is exactly the
//      slowdown this is supposed to avoid.
//   3. It skips anything already cached, and never re-runs a request that is
//      still in flight.
//
// A prefetch that fails is not an error. The tab will simply load normally
// when opened, which is what it did before any of this existed.

import { FeedItem, DEFAULT_FEED_FILTERS, FEED_PAGE_SIZE } from '../../types/socialFeed';
import { feedService } from './feedService';
import { exploreService, newExploreSeed } from './exploreService';
import { nearbyService, newNearbySeed, NearbyPosition } from './nearbyService';
import { momentsService } from './momentsService';
import { startLoop, tagCycle, advanceCursor, LoopCursor } from './feedLoop';
import { feedCacheKey, hasFreshFeedCache, writeFeedCache } from './feedCache';
import { logger } from '../../utils/logger';

export const EXPLORE_PAGE_SIZE = 40;
export const NEARBY_PAGE_SIZE = 20;

/** Surfaces that can be warmed ahead of time. */
export type PrefetchableTab = 'matches' | 'nearby' | 'explore' | 'stories';

const inFlight = new Set<string>();

/**
 * The device position, but only if reading it costs the viewer nothing.
 *
 * Prefetching must never surface a permission prompt: the viewer has not asked
 * for Nearby, and a location dialog appearing while they read a different tab
 * is indefensible. So this asks the Permissions API first and gives up unless
 * the answer is already "granted".
 */
async function positionWithoutPrompting(): Promise<NearbyPosition | null> {
  try {
    if (!navigator.permissions || !navigator.geolocation) return null;
    const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    if (status.state !== 'granted') return null;

    return await new Promise<NearbyPosition | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 5000, maximumAge: 5 * 60 * 1000 }
      );
    });
  } catch {
    return null;
  }
}

/**
 * Which Nearby list a position produces.
 *
 * Rounded to roughly a kilometre so ordinary GPS jitter still hits the same
 * entry, while a genuine move across town correctly misses it.
 */
export const nearbyVariant = (position: NearbyPosition | null): string =>
  position
    ? `geo:${position.latitude.toFixed(2)},${position.longitude.toFixed(2)}`
    : 'profile';

/** First page plus the cursor that follows it, in the shape containers hold. */
function firstPage<T extends { id: string }>(items: T[], seed: string, pageSize: number, nextSeed: () => string) {
  const cursor: LoopCursor = startLoop(seed);
  return {
    items: tagCycle(items, cursor.cycle),
    cursor: advanceCursor(cursor, items.length, pageSize, nextSeed),
  };
}

async function prefetchMatches(userId: string): Promise<void> {
  const key = feedCacheKey('matches', userId);
  if (hasFreshFeedCache(key)) return;

  const seed = Math.random().toString(36).slice(2, 10);
  const items = (await feedService.getFeedItems(userId, {
    page: 0,
    filters: DEFAULT_FEED_FILTERS,
    tab: 'matches',
    limit: FEED_PAGE_SIZE,
    seed,
  })) as FeedItem[];

  writeFeedCache(key, firstPage(items, seed, FEED_PAGE_SIZE, () => Math.random().toString(36).slice(2, 10)));
}

async function prefetchExplore(userId: string): Promise<void> {
  const key = feedCacheKey('explore', userId);
  if (hasFreshFeedCache(key)) return;

  const seed = newExploreSeed();
  const items = await exploreService.getExploreFeed(EXPLORE_PAGE_SIZE, 0, seed);
  writeFeedCache(key, firstPage(items, seed, EXPLORE_PAGE_SIZE, newExploreSeed));
}

async function prefetchNearby(userId: string): Promise<void> {
  const position = await positionWithoutPrompting();
  const key = feedCacheKey('nearby', userId, nearbyVariant(position));
  if (hasFreshFeedCache(key)) return;

  const seed = newNearbySeed();
  const items = await nearbyService.getNearbyFeed({
    position,
    limit: NEARBY_PAGE_SIZE,
    offset: 0,
    seed,
  });
  writeFeedCache(key, {
    ...firstPage(items, seed, NEARBY_PAGE_SIZE, newNearbySeed),
    meta: { usingProfileLocation: !position },
  });
}

async function prefetchMoments(userId: string): Promise<void> {
  const key = feedCacheKey('stories', userId);
  if (hasFreshFeedCache(key)) return;

  const reels = await momentsService.getMomentsFeed();
  // Moments is one request with no paging, so the cursor is inert. It is stored
  // anyway to keep every entry the same shape.
  writeFeedCache(key, { items: reels, cursor: startLoop('moments') });
}

const PREFETCHERS: Record<PrefetchableTab, (userId: string) => Promise<void>> = {
  matches: prefetchMatches,
  explore: prefetchExplore,
  nearby: prefetchNearby,
  stories: prefetchMoments,
};

/**
 * Warms the given tabs, in order, one at a time.
 *
 * Order is the caller's: pass the tabs adjacent to the active one first, since
 * those are the ones a swipe reaches next.
 */
export async function prefetchFeeds(userId: string, tabs: PrefetchableTab[]): Promise<void> {
  for (const tab of tabs) {
    const guard = `${tab}:${userId}`;
    if (inFlight.has(guard)) continue;
    inFlight.add(guard);
    try {
      await PREFETCHERS[tab](userId);
    } catch (error) {
      // Deliberately swallowed: the tab still loads normally when opened.
      logger.log(`[PREFETCH] ${tab} could not be warmed`, error);
    } finally {
      inFlight.delete(guard);
    }
  }
}

/**
 * Runs work when the browser is not busy.
 *
 * requestIdleCallback is the whole reason this does not cost startup time: it
 * will not fire while the page is still laying out and painting the tab the
 * viewer is looking at. The timeout is a floor for browsers that never go
 * idle, and the setTimeout is for Safari, which still has no idle callback.
 */
export function whenIdle(run: () => void, timeout = 2000): () => void {
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout: number }) => number)
    | undefined;

  if (ric) {
    const handle = ric(run, { timeout });
    return () => (window as any).cancelIdleCallback?.(handle);
  }
  const handle = window.setTimeout(run, Math.min(timeout, 1200));
  return () => window.clearTimeout(handle);
}
